import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { throwIfNotOk } from "@/lib/api-error";
import { formatDateWithOptionsBs } from "@/lib/format";
import type { CompanyBillingModel } from "@/types/company";
import type { CompanyInvoiceSummary, CompanyPaymentRequest } from "@/types/company-billing";
import { formatCoveredRange, type InvoicePeriodInput } from "@/lib/company-invoices/period";

/** Odgovor `GET /api/supervisor/billing`. */
export interface SupervisorBilling {
  companyName: string;
  billingModel: CompanyBillingModel;
  cofundingPercent: number;
  discountPercent: number;
  /** Lokalni datum `YYYY-MM-DD`. */
  contractExpiresAt: string | null;
  creditExpiresAt: string | null;
  paymentTermDays: number;
  /** KM, samo model kredita. */
  creditBalance: number | null;
  /** KM, samo model fakture: sve još nefakturisano do sada. */
  uninvoicedTotal: number | null;
  today: string;
  invoices: CompanyInvoiceSummary[];
}

export const SUPERVISOR_BILLING_KEY = ["supervisor", "billing"] as const;
export const SUPERVISOR_PAYMENT_REQUESTS_KEY = ["supervisor", "payment-requests"] as const;

export function useSupervisorBilling() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: SUPERVISOR_BILLING_KEY,
    queryFn: async () => {
      const res = await fetch("/api/supervisor/billing");
      await throwIfNotOk(res, t("common.errorLoading"));
      return res.json() as Promise<SupervisorBilling>;
    },
  });
}

export function useSupervisorPaymentRequests() {
  const { t } = useTranslation();
  return useQuery({
    queryKey: SUPERVISOR_PAYMENT_REQUESTS_KEY,
    queryFn: async () => {
      const res = await fetch("/api/supervisor/payment-requests");
      await throwIfNotOk(res, t("common.errorLoading"));
      return res.json() as Promise<CompanyPaymentRequest[]>;
    },
  });
}

const SHORT_DATE = { day: "numeric", month: "short", year: "numeric", timeZone: "Europe/Sarajevo" } as const;

/**
 * Stvarno obuhvaćen period fakture kao "1. okt 2026. - 31. okt 2026." u
 * Sarajevu (od najranije stavke kad je ranija od početka mjeseca). Faktura za
 * dopunu kredita nema period: datum dopune.
 */
export function formatInvoicePeriod(inv: InvoicePeriodInput): string {
  return formatCoveredRange(inv, (iso) => formatDateWithOptionsBs(iso, SHORT_DATE));
}

/** Datum instanta (createdAt i sl.) u Sarajevu. */
export function formatInstantDate(iso: string): string {
  return formatDateWithOptionsBs(iso, SHORT_DATE);
}
